import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './OrganizationDocumentsPage.module.css';
import { OrganizationDocumentsTemplate } from '../../templates';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { FileMeta } from '../../../store/mediaAPI/document/dto/request/document-save-request';
import {
  deleteDocument,
  fetchDocumentById,
  saveBuyerDocument,
  updateDocument,
} from '../../../store/buyerAPI/buyer/reducers/buyer-document.slice';
import { DocumentSaveResponseDto } from '../../../store/mediaAPI/document/dto/response/document-save-response';
import { getUUID } from '../../../utils/UUID';
import { BuyerDocumentSaveRequestDto } from '../../../store/buyerAPI/buyer/dto/request/buyer-document-save-request.dto';
import Spinner from '../../atoms/Spinner/Spinner';
import { saveDocument } from '../../../store/mediaAPI/document/reducers/document.slice';
import { fetchBuyerById } from '../../../store/buyerAPI/buyer/reducers/buyer.slice';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import { BuyerDocumentResponseDto } from '../../../store/buyerAPI/buyer/dto/response/buyer-document-response.dto';
import { BuyerDocumentUpdateRequestDto } from '../../../store/buyerAPI/buyer/dto/request/buyer-document-update-request.dto';
import { EditOrganizationDocumentModal } from '../../organisms';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';

interface OrganizationDocumentsPageProps {}

const OrganizationDocumentsPage: FC<OrganizationDocumentsPageProps> = () => {
  const { organizationId } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const [file, setFile] = useState<string>();
  const [fileMeta, setFileMeta] = useState<FileMeta>();
  const [fileName, setFileName] = useState<string>();
  const [documents, setDocuments] = useState<BuyerDocumentResponseDto[]>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [documentToEdit, setDocumentToEdit] =
    useState<BuyerDocumentResponseDto>();
  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const [documentId, setDocumentId] = useState<string>();

  const { status } = useSelector(
    (state: RootState) => state.buyerDocumentSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  useEffect(() => {
    if (!documentId) {
      return;
    }

    dispatch(
      fetchDocumentById({
        documentId: documentId,
        language: selectedLanguage,
      })
    )
      .unwrap()
      .then((resp) => setDocumentToEdit(resp));
  }, [documentId, dispatch, selectedLanguage]);

  useEffect(() => {
    dispatch(
      fetchBuyerById({
        id: organizationId!,
        language: LocalesLanguagesConstants.en,
      })
    )
      .unwrap()
      .then((resp) => setDocuments(resp.documents));
  }, [organizationId, dispatch, setDocuments]);

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!documentToEdit || !organizationId) return;
    const params: TranslateParams<BuyerDocumentResponseDto> = {
      obj: documentToEdit,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translatedResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as BuyerDocumentResponseDto;

    const updateRequest: BuyerDocumentUpdateRequestDto = {
      organizationId: organizationId,
      file: documentToEdit.file ?? '',
      name: translatedResponse.name ?? '',
      id: documentToEdit.id,
      published: documentToEdit.published,
    };
    await dispatch(
      updateDocument({
        data: updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  const handleDelete = async (documentId: string) => {
    await dispatch(deleteDocument(documentId));
    dispatch(
      fetchBuyerById({
        id: organizationId!,
        language: LocalesLanguagesConstants.en,
      })
    )
      .unwrap()
      .then((resp) => setDocuments(resp.documents));
  };

  const handleDownload = async (documentId: string) => {
    const documentToDownload = (
      await dispatch(
        fetchDocumentById({
          documentId: documentId,
          language: selectedLanguage,
        })
      )
    ).payload as BuyerDocumentResponseDto;

    const link = document.createElement('a');
    const fileUrl = documentToDownload.file ?? '';
    link.href = documentToDownload.file ?? '';
    link.download = documentToDownload.name ?? '';
    link.target = '_blank';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(fileUrl);
  };

  const handleEditAction = (id: string) => {
    setDocumentId(id);
    setShowModal(true);
  };

  const handleOnChange = async (document: BuyerDocumentResponseDto) => {
    setDocumentToEdit(document);
  };

  const handleEdit = async (document: BuyerDocumentResponseDto) => {
    const documentToUpdate: BuyerDocumentUpdateRequestDto = {
      organizationId: organizationId ?? '',
      file: document.file ?? '',
      name: document.name ?? '',
      id: document.id,
      published: document.published,
    };
    await dispatch(
      updateDocument({ data: documentToUpdate, language: selectedLanguage })
    );
    dispatch(
      fetchBuyerById({
        id: organizationId!,
        language: LocalesLanguagesConstants.en,
      })
    )
      .unwrap()
      .then((resp) => setDocuments(resp.documents));
    setShowModal(false);
  };

  const handleFileOnChange = (file: string) => {
    setFile(file);
  };

  const handleFileMetaOnChange = (fileMeta: FileMeta) => {
    setFileMeta(fileMeta);
  };

  const handleFileNameOnChange = (fileName: string) => {
    setFileName(fileName);
  };

  const saveFile = async (
    fileUrl: string | undefined,
    fileMeta: FileMeta | undefined
  ): Promise<DocumentSaveResponseDto> => {
    if (fileUrl && fileUrl !== '' && fileMeta) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([fileUrl], { type: fileMeta.fileType ?? 'image/png' }),
        fileMeta.fileSrc
      );
      return (await dispatch(saveDocument(formData)))
        .payload as DocumentSaveResponseDto;
    }
    return { href: '' };
  };

  const callbackFn = async () => {
    const savedFile = await saveFile(file, fileMeta);
    const buyerDocumentToSave: BuyerDocumentSaveRequestDto = {
      name: fileName ?? '',
      id: getUUID(),
      published: true,
      organizationId: organizationId ?? '',
      file: savedFile.href,
    };
    await dispatch(
      saveBuyerDocument({
        data: buyerDocumentToSave,
        language: selectedLanguage,
      })
    );
    dispatch(
      fetchBuyerById({
        id: organizationId!,
        language: LocalesLanguagesConstants.en,
      })
    )
      .unwrap()
      .then((resp) => setDocuments(resp.documents));
  };

  if (status === 'loading' || translateStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      {showModal && documentToEdit && (
        <EditOrganizationDocumentModal
          show={showModal}
          setShow={setShowModal}
          handleEdit={handleEdit}
          handleOnChange={handleOnChange}
          document={documentToEdit}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
      <OrganizationDocumentsTemplate
        documents={documents ?? []}
        handleDelete={handleDelete}
        handleDownload={handleDownload}
        handleEdit={handleEditAction}
        handleFileMetaOnChange={handleFileMetaOnChange}
        handleFileOnChange={handleFileOnChange}
        handleFileNameOnChange={handleFileNameOnChange}
        callbackFn={callbackFn}
      />
    </div>
  );
};

export default OrganizationDocumentsPage;
